// Helpers for deciding whether a room option still matches what's saved on the
// itinerary. When the live selection (dates / occupancy / charter inputs) matches the
// saved booking we keep the "Already in trip" / "Selected" treatment; once any dimension
// diverges we drop it so the user can update the itinerary.
//
// Shared by the hotel listing drawers and the chat room-options widget — the two
// surfaces answer the same question and drifted apart once already.

function sameChildAges(a?: Array<number>, b?: Array<number>): boolean {
  const aa = [...(a ?? [])].sort((x, y) => x - y)
  const bb = [...(b ?? [])].sort((x, y) => x - y)
  return aa.length === bb.length && aa.every((value, index) => value === bb[index])
}

function sameRoom(a: App.Occupants, b: App.Occupants): boolean {
  return (
    a.adults === b.adults &&
    (a.children ?? 0) === (b.children ?? 0) &&
    (a.infants ?? 0) === (b.infants ?? 0) &&
    (a.teenagers ?? 0) === (b.teenagers ?? 0) &&
    sameChildAges(a.childrenAge, b.childrenAge)
  )
}

/**
 * Compares the live room occupancy against the occupancy saved on the itinerary.
 * When nothing was saved we can't detect a change, so we treat it as a match to
 * preserve the prior date-only behaviour (i.e. don't block on occupancy).
 */
export function occupanciesMatch(
  saved: Array<App.Occupants> | undefined,
  live: Array<App.Occupants> | undefined,
): boolean {
  if (!saved?.length) return true
  if (!live || live.length !== saved.length) return false
  return saved.every((room, index) => sameRoom(room, live[index]))
}

interface CharterSelection {
  originAirportCode?: string
  fareClass?: string
}

/**
 * Compares the live charter selection (Maldives direct) against what was saved.
 * Only gates on a field that was actually saved, so non-charter hotels — which
 * carry no saved charter values — always match.
 */
export function charterSelectionMatches(saved: CharterSelection, live: CharterSelection): boolean {
  if (saved.originAirportCode && saved.originAirportCode !== live.originAirportCode) return false
  if (saved.fareClass && saved.fareClass !== live.fareClass) return false
  return true
}

/** The identifiers a saved booking, an assistant selection and a priced rate share. */
export interface RateIdentifiers {
  packageId?: string
  roomRateId?: string
}

/**
 * Whether a saved booking (or an assistant selection) names this exact rate.
 *
 * The identifiers are constraints, not alternatives — every one the two sides both
 * carry has to agree, and at least one has to be comparable. Three traps sit behind
 * that:
 *
 * - Matching on `packageId` alone marks every cancellation variant of that package.
 * - Matching on `roomRateId` alone marks every package sharing that rate. `roomRateId`
 *   is not unique per package on an LE offer: an upgrade package layers extra
 *   inclusions onto the same underlying room rate, so a base package and its upgrade
 *   share one.
 * - Skipping a check whenever either side omits the id means two rates that carry no
 *   identifier in common "agree" vacuously — an unrelated rate then reads as selected.
 *   Requiring at least one comparable id rules that out, while still resolving a
 *   bedbank rate that carries only a `roomRateId`.
 */
export function selectionMatchesRate(selection: RateIdentifiers, rate: RateIdentifiers): boolean {
  const packageIdsComparable = !!selection.packageId && !!rate.packageId
  const rateIdsComparable = !!selection.roomRateId && !!rate.roomRateId
  if (!packageIdsComparable && !rateIdsComparable) return false
  if (packageIdsComparable && selection.packageId !== rate.packageId) return false
  if (rateIdsComparable && selection.roomRateId !== rate.roomRateId) return false
  return true
}

interface SavedStay {
  checkIn?: string
  checkOut?: string
  occupancies?: Array<App.Occupants>
}

interface LiveStay {
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
}

/**
 * Whether the stay currently being priced is still the one saved on the itinerary.
 *
 * The saved room only keeps its "Selected" treatment while the dates and guests match
 * what's saved; once either changes the traveller is asking for a *different* stay and
 * needs the button back to update it. Mirrors the drawer's `isSavedSelection` gate.
 *
 * A dimension either side omits is skipped rather than failed: an undated trip saves no
 * check-in, and duration-only pricing returns none, so gating on an absent date would
 * strip the treatment from rooms that really are saved.
 */
export function savedStayMatches(saved: SavedStay, live: LiveStay): boolean {
  if (saved.checkIn && live.checkIn && saved.checkIn !== live.checkIn) return false
  if (saved.checkOut && live.checkOut && saved.checkOut !== live.checkOut) return false
  return occupanciesMatch(saved.occupancies, live.rooms)
}
